import { useState } from "react";

import { Button, CloseSVG, ChevronLeftSVG, FilesSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import { LessonSparkAttachment } from "@/bundles/types/LessonSparkAttachment";

import AttachmentItem from "../AttachmentItem";
import {
  panelContainerCss,
  iconCss,
  headerCss,
  sectionCss,
  itemCss,
  itemIconCss,
  headerTitleCss,
} from "./AttachmentPanel.styles";

const CONTENT_TYPE_TITLE_MAPPING: Record<string, string> = {
  LESSON_SPARK: "Lesson Spark",
};

const ButtonWithTrackEvent = withTrackEvent(Button);

interface AttachmentPanelProps {
  attachmentData: LessonSparkAttachment[];
  onClose: () => void;
}

const AttachmentPanel = ({ attachmentData, onClose }: AttachmentPanelProps) => {
  const [selectedResource, setSelectedResource] =
    useState<LessonSparkAttachment | null>(null);

  if (!attachmentData || !attachmentData?.length) return null;

  const onClickResourceItem = (id: string) => {
    const selectedItem = attachmentData.find(
      (attachment) => attachment.id === id,
    ) as LessonSparkAttachment;
    setSelectedResource(selectedItem);
  };

  return (
    <div css={panelContainerCss}>
      <div css={headerCss}>
        {/* Resource panel header */}
        {selectedResource ? (
          <ButtonWithTrackEvent
            ariaProps={{ "aria-label": "Go back to resource list" }}
            dataProps={{
              "data-qa-selector":
                "go-back-to-resource-list-bundle-page-resource-panel",
            }}
            flavor={Button.flavors.incognito}
            onClick={() => setSelectedResource(null)}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            streamProperties={{
              description: "Go back to resource list on bundle page",
            }}
          >
            <ChevronLeftSVG css={iconCss} />
          </ButtonWithTrackEvent>
        ) : null}

        <h2 css={headerTitleCss}>
          {selectedResource
            ? CONTENT_TYPE_TITLE_MAPPING?.[selectedResource.content_type]
            : "Resources"}
        </h2>

        <ButtonWithTrackEvent
          ariaProps={{ "aria-label": "Close resource panel" }}
          dataProps={{ "data-qa-selector": "close-bundle-page-resource-panel" }}
          flavor={Button.flavors.incognito}
          onClick={onClose}
          actionName={actionNames.interact}
          objectType={objectTypes.content}
          streamProperties={{
            description: "Close bundles page resource panel",
          }}
        >
          <CloseSVG css={iconCss} />
        </ButtonWithTrackEvent>
      </div>

      {/* Resource list view */}
      {!selectedResource ? (
        <>
          {attachmentData?.map((attachment: LessonSparkAttachment, index) => {
            return (
              <div key={index} css={sectionCss}>
                <ButtonWithTrackEvent
                  flavor={Button.flavors.incognito}
                  __cssFor={{
                    root: itemCss,
                  }}
                  onClick={() => onClickResourceItem(attachment.id)}
                  actionName={actionNames.interact}
                  objectType={objectTypes.content}
                  streamProperties={{
                    description: "Select a bundle resource from the panel",
                  }}
                >
                  <FilesSVG css={itemIconCss} />
                  {attachment?.data?.name || ""}
                </ButtonWithTrackEvent>
              </div>
            );
          })}
        </>
      ) : null}

      {/* Single resource item view */}
      {selectedResource ? (
        <AttachmentItem selectedResource={selectedResource} />
      ) : null}
    </div>
  );
};

export default AttachmentPanel;
